import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import moment from 'moment';

import { getCroAssessmentReport } from '../../../../redux/features/croSlice';

/**
 * Full assessment rubric for one student, loaded on demand when the CRO clicks
 * View. Renders assessment -> subject -> level -> objective -> checked
 * descriptions, the same shape the student portal shows.
 */
export default function AssessmentReportContent({ admnNo, studentName }) {
  const dispatch = useDispatch();
  const { croAssessmentReport, croAssessmentReportLoading } = useSelector((state) => state.cro);

  useEffect(() => {
    if (admnNo) dispatch(getCroAssessmentReport(admnNo));
  }, [admnNo, dispatch]);

  if (croAssessmentReportLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading assessment report…
        </Typography>
      </Stack>
    );
  }

  if (!croAssessmentReport?.length) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }} gap={1}>
        <Icon icon="mdi:file-search-outline" width="42" height="42" color="#9ca3af" />
        <Typography color="text.secondary">
          No assessment report found for {studentName || 'this student'}.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      {croAssessmentReport.map((assessment) => {
        const details = assessment?.studentDetails ?? {};
        return (
          <Box key={details.assessmentID}>
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              gap={1}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {details.courseFullName || 'Assessment'}
              </Typography>
              {details.dateOfAssessment && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={moment(details.dateOfAssessment).format('DD MMM YYYY')}
                />
              )}
              {details.courseDuration && (
                <Chip size="small" variant="outlined" label={details.courseDuration} />
              )}
              {details.combination && (
                <Chip size="small" variant="outlined" label={details.combination} />
              )}
            </Stack>

            {details.remarks && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <strong>Remarks:</strong> {details.remarks}
              </Typography>
            )}

            {(assessment?.description ?? []).map((subject) => (
              <Accordion key={`${details.assessmentID}-${subject.subjectId}`} disableGutters>
                <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
                  <Typography sx={{ fontWeight: 600 }}>{subject.subject}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={2}>
                    {(subject?.levels ?? []).map((level) => (
                      <Box key={`${subject.subjectId}-${level.levelId}`}>
                        {level.level && (
                          <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                          >
                            {level.level}
                          </Typography>
                        )}
                        <Stack gap={1.5} sx={{ mt: 1 }}>
                          {(level?.objectives ?? []).map((objective) => (
                            <Box key={`${level.levelId}-${objective.id}`}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={1}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {objective.name || '—'}
                                </Typography>
                                <StarRating value={objective.starRating} />
                              </Stack>
                              <Stack component="ul" sx={{ pl: 3, m: 0, mt: 0.5 }}>
                                {(objective?.checkedDescriptions ?? []).map((desc) => (
                                  <Typography
                                    component="li"
                                    variant="body2"
                                    color="text.secondary"
                                    key={desc.id}
                                  >
                                    {desc.description}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * grade_name carries the score. Historically it is a small star count, but rows
 * scored out of 10 also exist, so anything above 5 is shown as a plain value
 * instead of being silently clamped to a 5 star display.
 */
const StarRating = ({ value }) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return value ? <Chip size="small" variant="outlined" label={value} /> : null;
  }
  if (numeric > 5) {
    return <Chip size="small" variant="outlined" label={`Score: ${numeric}`} />;
  }
  return <Rating value={numeric} precision={0.5} readOnly size="small" />;
};
